import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Pagination
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function SuiviStage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1');
  const itemsPerPage = 2;
  const [etudiant, setEtudiant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [candidatures, setCandidatures] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les détails de l'étudiant
        const etudiantResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!etudiantResponse.ok) {
          throw new Error("Impossible de récupérer les données de l'étudiant");
        }

        const data = await etudiantResponse.json();
        // Ajuster la structure selon la réponse de l'API
        setEtudiant({
          id: data.etudiant.id_etudiants,
          nom: data.etudiant.user.nom,
          prenom: data.etudiant.user.prenom,
          email: data.etudiant.user.email,
          formation: data.etudiant.formation,
          specialite: data.etudiant.specialite,
          annee: data.etudiant.annee,
          competences: data.etudiant.competences || []
        });

        // Récupérer les candidatures
        const candidaturesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiants/${id}/candidatures`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!candidaturesResponse.ok) {
          throw new Error("Impossible de récupérer les candidatures");
        }

        const candidaturesData = await candidaturesResponse.json();
        setCandidatures(candidaturesData.candidatures);
        setTotalPages(Math.ceil(candidaturesData.candidatures.length / itemsPerPage));

      } catch (err) {
        console.error("Erreur lors du chargement des données:", err);
        setError(err.message || "Une erreur est survenue");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getCurrentPageCandidatures = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return candidatures.slice(startIndex, endIndex);
  };

  const handlePageChange = (event, value) => {
    setSearchParams({ page: value.toString() });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/etudiants')}
        variant="contained"
        color="primary"
        sx={{ mb: 4 }}
      >
        Retour aux étudiants
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        Suivi de stage - {etudiant?.prenom} {etudiant?.nom}
      </Typography>

      <Grid container spacing={3} sx={{ height: '100%' }}>
        {/* Informations de l'étudiant */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Informations de l'étudiant
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography><strong>Email :</strong> {etudiant?.email}</Typography>
              <Typography><strong>Formation :</strong> {etudiant?.formation}</Typography>
              <Typography><strong>Spécialité :</strong> {etudiant?.specialite}</Typography>
              <Typography><strong>Année :</strong> {etudiant?.annee}</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Compétences :</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {etudiant?.competences?.map((comp, index) => (
                    <Chip 
                      key={index}
                      label={comp.nom_comp}
                      size="small"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* État de la recherche */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                État de la recherche de stage
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {candidatures.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Grid container spacing={2}>
                    {getCurrentPageCandidatures().map((candidature) => (
                      <Grid item xs={12} sm={6} key={candidature.id_candidature}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            p: 2,
                            cursor: 'pointer',
                            borderLeft: 6,
                            borderColor: 
                              candidature.statut === 'acceptee' ? 'success.main' :
                              candidature.statut === 'refusee' ? 'error.main' :
                              'primary.main',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                          onClick={() => navigate(`/offres/${candidature.offre.id_offre}`)}
                        >
                          <Typography variant="h6" component="div" noWrap>
                            {candidature.offre?.titre || 'Titre non disponible'}
                          </Typography>
                          <Typography color="textSecondary" gutterBottom noWrap>
                            {candidature.offre?.entreprise?.nom || 'Entreprise non disponible'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip 
                              label={
                                candidature.statut === 'acceptee' ? 'Acceptée' :
                                candidature.statut === 'refusee' ? 'Refusée' :
                                'En attente'
                              }
                              color={
                                candidature.statut === 'acceptee' ? 'success' :
                                candidature.statut === 'refusee' ? 'error' :
                                'warning'
                              }
                              size="small"
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>
                              Postulé le {new Date(candidature.date_postulation).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                      <Pagination 
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  Aucune candidature pour le moment
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default SuiviStage; 